import { products } from "./catalog";
import type {
  Evidence,
  SearchFilters,
  SearchResult,
  Store,
  StoreMatch,
  Match,
} from "./types";
export const HOUR = 3600000;
const variantKey = (variant?: string) =>
  variant?.trim().toLowerCase() || undefined;
function negativeApplies(positive: Evidence, negative: Evidence) {
  const positiveVariant = variantKey(positive.variant),
    negativeVariant = variantKey(negative.variant);
  return (
    !positiveVariant || !negativeVariant || positiveVariant === negativeVariant
  );
}
export function milesBetween(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
) {
  const rad = (v: number) => (v * Math.PI) / 180,
    dlat = rad(b.latitude - a.latitude),
    dlon = rad(b.longitude - a.longitude);
  const h =
    Math.sin(dlat / 2) ** 2 +
    Math.cos(rad(a.latitude)) *
      Math.cos(rad(b.latitude)) *
      Math.sin(dlon / 2) ** 2;
  return (
    3958.7613 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)))
  );
}
export function selectedProductIds(filters: SearchFilters) {
  if (!filters.product_ids.length && !filters.category_ids.length)
    return new Set(products.map((p) => p.id));
  return new Set([
    ...filters.product_ids,
    ...products
      .filter(
        (p) =>
          filters.category_ids.includes("jellycat_bag_charm") &&
          p.brand === "Jellycat" &&
          p.category === "bag_charm",
      )
      .map((p) => p.id),
  ]);
}
export function searchEvidence(
  stores: Store[],
  evidence: Evidence[],
  filters: SearchFilters,
  now = new Date(),
): SearchResult {
  const selected = selectedProductIds(filters),
    clock = now.getTime();
  const selectedBrands = new Set(
    products.filter((p) => selected.has(p.id)).map((p) => p.brand),
  );
  const visible = evidence.filter(
    (e) =>
      e.moderation === "approved" &&
      !e.online_only &&
      !e.synthetic &&
      !filters.blocked_contributors?.includes(e.contributor_id ?? "") &&
      (!e.observed_at || Date.parse(e.observed_at) <= clock),
  );
  const hits: StoreMatch[] = [];
  for (const store of stores) {
    const distance = milesBetween(filters.center, store),
      b = filters.bounds;
    if (
      (filters.scope !== "us" && distance > filters.radius_miles) ||
      (b &&
        (store.latitude < b.south ||
          store.latitude > b.north ||
          (b.west > b.east
            ? store.longitude < b.west && store.longitude > b.east
            : store.longitude < b.west || store.longitude > b.east)))
    )
      continue;
    const atStore = visible.filter((e) => e.store_id === store.id),
      matches: Match[] = [];
    for (const pid of selected) {
      const variant = variantKey(filters.variants?.[pid]);
      const observations = atStore
        .filter(
          (e) =>
            e.product_id === pid &&
            ["community", "retailer"].includes(e.source_kind) &&
            e.observed_at,
        )
        .sort(
          (a, b) =>
            Date.parse(b.observed_at!) - Date.parse(a.observed_at!) ||
            Number(a.outcome === "saw_it") - Number(b.outcome === "saw_it") ||
            a.id.localeCompare(b.id),
        );
      const positives = observations.filter(
        (e) =>
          e.outcome === "saw_it" &&
          (!variant || variantKey(e.variant) === variant),
      );
      const negatives = observations.filter((e) => e.outcome !== "saw_it");
      const latest = positives.find(
        (positive) =>
          !negatives.some(
            (negative) =>
              negativeApplies(positive, negative) &&
              Date.parse(negative.observed_at!) >=
                Date.parse(positive.observed_at!),
          ),
      );
      if (latest) {
        const recent = clock - Date.parse(latest.observed_at!) <= 24 * HOUR;
        if (latest.outcome === "saw_it" && (recent || filters.layers.older))
          matches.push({
            product_id: pid,
            state: recent ? "recent" : "older",
            observed_at: latest.observed_at,
            source_kind: latest.source_kind,
            evidence_id: latest.id,
            variant: latest.variant,
            source_url: latest.source_url,
          });
      } else if (filters.layers.older && positives.length) {
        const conflict = negatives.find((negative) =>
          positives.some(
            (positive) =>
              negativeApplies(positive, negative) &&
              Date.parse(negative.observed_at!) >=
                Date.parse(positive.observed_at!),
          ),
        );
        if (conflict)
          matches.push({
            product_id: pid,
            state: "conflicting",
            observed_at: conflict.observed_at,
            source_kind: conflict.source_kind,
            evidence_id: conflict.id,
          });
      }
      if (filters.layers.mentions) {
        const mention = atStore.find(
          (e) =>
            e.product_id === pid &&
            e.source_kind === "public_mention" &&
            (!variant || variantKey(e.variant) === variant) &&
            e.outcome === "saw_it",
        );
        if (mention)
          matches.push({
            product_id: pid,
            state: "mention",
            observed_at: mention.observed_at,
            source_kind: "public_mention",
            source_url: mention.source_url,
            evidence_id: mention.id,
          });
      }
    }
    if (
      filters.layers.stockists &&
      store.brands.some((b) => selectedBrands.has(b as "NeeDoh" | "Jellycat"))
    )
      matches.push({
        product_id: null,
        state: "stockist",
        observed_at: null,
        source_kind: "stockist",
        source_url: store.source_url,
      });
    if (matches.length)
      hits.push({
        ...store,
        matches,
        distance_miles: distance,
        recent_count: matches.filter((m) => m.state === "recent").length,
      });
  }
  hits.sort(
    (a, b) =>
      Number(b.recent_count > 0) - Number(a.recent_count > 0) ||
      a.distance_miles - b.distance_miles,
  );
  const clusters: SearchResult["clusters"] = [];
  if ((filters.zoom ?? 10) < 6) {
    const cells = new Map<string, StoreMatch[]>(),
      size = (filters.zoom ?? 4) < 4 ? 12 : 4;
    for (const s of hits) {
      const k = `${Math.floor(s.latitude / size)}:${Math.floor(s.longitude / size)}`;
      cells.set(k, [...(cells.get(k) ?? []), s]);
    }
    for (const [id, group] of cells)
      clusters.push({
        id,
        latitude: group.reduce((a, b) => a + b.latitude, 0) / group.length,
        longitude: group.reduce((a, b) => a + b.longitude, 0) / group.length,
        count: group.length,
      });
  }
  const offset = Math.max(0, filters.offset ?? 0),
    limit = Math.min(100, filters.limit ?? 100);
  return {
    stores: hits.slice(offset, offset + limit),
    clusters,
    total_matches: hits.length,
    recent_stores: hits.filter((s) => s.recent_count > 0).length,
    generated_at: now.toISOString(),
    has_more: hits.length > offset + limit,
  };
}
